import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { FmButton } from './FmButton';
import { useFmMotion } from './FmMotionHelper';
import './FmCarousel.css';

export interface FmCarouselSlide {
  id: string;
  content: React.ReactNode;
  'data-testid'?: string;
}

export interface FmCarouselLabels {
  /** Accessible name of the carousel region. */
  carousel: string;
  previous: string;
  next: string;
  /** Slide position, with `{index}` and `{count}` placeholders, e.g. "{index} of {count}". */
  slide: string;
}

export interface FmCarouselProps {
  slides: FmCarouselSlide[];
  labels: FmCarouselLabels;
  /** Notified with the id of the slide that comes into view. */
  onSlideChange?: (slideId: string) => void;
  /**
   * @internal DS-internal className prop; forbidden in route-owned code.
   */
  className?: string;
  'data-testid'?: string;
}

const cx = (...parts: Array<string | false | null | undefined>): string =>
  parts.filter(Boolean).join(' ');

// Widgets that own their arrow keys keep them when they sit inside a slide.
const ARROW_KEY_OWNERS =
  'input, textarea, select, [contenteditable=""], [contenteditable="true"], [role="tablist"], [role="radiogroup"], [role="listbox"], [role="menu"], [role="menubar"], [role="grid"], [role="slider"], [role="tree"], [role="combobox"]';

/**
 * Slides this far from the one in view keep their content mounted. A carousel
 * of one chart per stand otherwise drew every chart up front (22 for one
 * sale) to show one; the neighbours stay ready so a move never shows a blank.
 */
const MOUNTED_NEIGHBOURS = 1;

function slideLabel(template: string, index: number, count: number): string {
  return template.replace('{index}', String(index + 1)).replace('{count}', String(count));
}

/**
 * FmCarousel — Embla scrolling with what Embla does not ship: visible
 * previous/next buttons, carousel and slide semantics with "n of m" labels,
 * arrow-key navigation, no autoplay, and jumps instead of animation when the
 * reader prefers reduced motion.
 */
export const FmCarousel: React.FC<FmCarouselProps> = ({
  slides,
  labels,
  onSlideChange,
  className,
  'data-testid': dataTestId,
}) => {
  const { canAnimate } = useFmMotion('section');
  const [viewportRef, embla] = useEmblaCarousel({ align: 'start', loop: false });
  const [selected, setSelected] = useState(0);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(slides.length > 1);
  const rootRef = useRef<HTMLElement>(null);
  const previousRef = useRef<HTMLButtonElement>(null);
  const nextRef = useRef<HTMLButtonElement>(null);
  const focusHandoffRef = useRef<HTMLButtonElement | null>(null);
  // Consumers build slides inline; refs keep the Embla subscription stable
  // across renders so onSlideChange fires only when the slide changes.
  const slidesRef = useRef(slides);
  slidesRef.current = slides;
  const onSlideChangeRef = useRef(onSlideChange);
  onSlideChangeRef.current = onSlideChange;
  const jump = !canAnimate;

  useEffect(() => {
    if (!embla) return undefined;
    const sync = (): void => {
      const index = embla.selectedScrollSnap();
      setSelected(index);
      // Off-screen slides leave the tab order and the accessibility tree, so
      // focus cannot scroll the clipped viewport behind Embla's selected index.
      embla.slideNodes().forEach((node, nodeIndex) => {
        node.toggleAttribute('inert', nodeIndex !== index);
      });
      const prevAvailable = embla.canScrollPrev();
      const nextAvailable = embla.canScrollNext();
      // A control that becomes disabled drops focus to the document body, which
      // would strand keyboard users outside the carousel; the control that still
      // moves takes focus once the commit has enabled it.
      const focused = document.activeElement;
      if (focused === nextRef.current && !nextAvailable) {
        focusHandoffRef.current = previousRef.current;
      } else if (focused === previousRef.current && !prevAvailable) {
        focusHandoffRef.current = nextRef.current;
      }
      setCanPrev(prevAvailable);
      setCanNext(nextAvailable);
      const slide = slidesRef.current[index];
      if (slide) onSlideChangeRef.current?.(slide.id);
    };
    sync();
    embla.on('select', sync);
    embla.on('reInit', sync);
    return () => {
      embla.off('select', sync);
      embla.off('reInit', sync);
    };
  }, [embla]);

  useLayoutEffect(() => {
    const target = focusHandoffRef.current;
    focusHandoffRef.current = null;
    target?.focus();
  }, [canPrev, canNext]);

  // Arrow keys move slides while focus is inside the carousel, unless a child
  // widget already handled them or owns them; the listener is native because
  // the region itself is not an interactive element.
  useEffect(() => {
    const root = rootRef.current;
    if (!root || !embla) return undefined;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.defaultPrevented) return;
      if (event.target instanceof Element && event.target.closest(ARROW_KEY_OWNERS)) return;
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        embla.scrollPrev(jump);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        embla.scrollNext(jump);
      }
    };
    root.addEventListener('keydown', onKeyDown);
    return () => root.removeEventListener('keydown', onKeyDown);
  }, [embla, jump]);

  const previous = (): void => embla?.scrollPrev(jump);
  const next = (): void => embla?.scrollNext(jump);

  return (
    <section
      ref={rootRef}
      className={cx('fm-carousel', className)}
      aria-roledescription="carousel"
      aria-label={labels.carousel}
      data-testid={dataTestId}
    >
      <div className="fm-carousel__viewport" ref={viewportRef}>
        <div className="fm-carousel__track">
          {slides.map((slide, index) => (
            <fieldset
              key={slide.id}
              className="fm-carousel__slide"
              aria-roledescription="slide"
              aria-label={slideLabel(labels.slide, index, slides.length)}
              data-active={index === selected ? 'true' : 'false'}
              data-testid={slide['data-testid']}
            >
              {Math.abs(index - selected) <= MOUNTED_NEIGHBOURS ? slide.content : null}
            </fieldset>
          ))}
        </div>
      </div>
      {slides.length > 1 ? (
        <div className="fm-carousel__controls">
          <FmButton
            ref={previousRef}
            variant="bordered"
            size="sm"
            aria-label={labels.previous}
            title={labels.previous}
            disabled={!canPrev}
            onClick={previous}
          >
            <LeftOutlined />
          </FmButton>
          <span className="fm-carousel__status" aria-live="polite">
            {slideLabel(labels.slide, selected, slides.length)}
          </span>
          <FmButton
            ref={nextRef}
            variant="bordered"
            size="sm"
            aria-label={labels.next}
            title={labels.next}
            disabled={!canNext}
            onClick={next}
          >
            <RightOutlined />
          </FmButton>
        </div>
      ) : null}
    </section>
  );
};

FmCarousel.displayName = 'FmCarousel';
